import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Plus, ExternalLink, Power, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DealersManager = () => {
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', mobile: '', password: '' });

  const headers = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/dealers`, { headers: headers() });
      setDealers(res.data.dealers || []);
    } catch (err) {
      toast({ title: 'Failed to load dealers', description: err.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.mobile || !form.password) {
      toast({ title: 'All fields required', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/dealers`, form, { headers: headers() });
      toast({ title: 'Dealer created' });
      setCreateOpen(false);
      setForm({ name: '', email: '', mobile: '', password: '' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const toggleActive = async (dealer) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/dealers/${dealer.id}/active`,
        null,
        { headers: headers(), params: { is_active: !dealer.is_active } }
      );
      toast({ title: dealer.is_active ? 'Dealer deactivated' : 'Dealer activated' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const removeDealer = async (id) => {
    if (!window.confirm('Delete this dealer permanently? Their listings will remain in the database.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/dealers/${id}`, { headers: headers() });
      toast({ title: 'Dealer deleted' });
      load();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Dealers</CardTitle>
        <Button onClick={() => setCreateOpen(true)} data-testid="admin-create-dealer-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> New Dealer
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-12 text-center text-slate-500">Loading...</div>
        ) : dealers.length === 0 ? (
          <div className="py-12 text-center text-slate-500">No dealers yet. Create the first one above.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wider text-slate-500">
                  <th className="text-left p-3">Name</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">Mobile</th>
                  <th className="text-left p-3">Slug</th>
                  <th className="text-left p-3">Listings</th>
                  <th className="text-left p-3">Sold</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {dealers.map((d) => (
                  <tr key={d.id} className="border-b" data-testid={`dealer-row-${d.id}`}>
                    <td className="p-3 font-medium">{d.name}</td>
                    <td className="p-3 text-slate-600">{d.email}</td>
                    <td className="p-3 text-slate-600">{d.mobile}</td>
                    <td className="p-3">
                      <a href={`/dealers/${d.slug}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1">
                        {d.slug} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="p-3">{d.active_listings || 0}</td>
                    <td className="p-3">{d.cars_sold || 0}</td>
                    <td className="p-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {d.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <div className="inline-flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => toggleActive(d)} data-testid={`toggle-${d.id}`}>
                          <Power className="w-3 h-3 mr-1" /> {d.is_active ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => removeDealer(d.id)} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Dealer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Dealer Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-dealer-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Email *</label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-dealer-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Mobile *</label>
              <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} maxLength={15} data-testid="new-dealer-mobile" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">Initial Password *</label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-dealer-password" />
              <p className="text-[11px] text-slate-400 mt-1">Share this with the dealer. They can change it later.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-dealer" className="bg-blue-600 hover:bg-blue-700 text-white">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default DealersManager;
